import { AlertTriangle, ArrowRight, Inbox, Sparkles } from "lucide-react";
import type { JSX } from "react";
import { Link } from "react-router";

import type { IssueInfo } from "../../lib/api/issues.js";
import { Button } from "../ui/button.js";

import {
  ISSUE_TYPE_ICON,
  ISSUE_TYPE_LABEL,
  SEVERITY_STYLES,
} from "./issue-presentation.js";

/**
 * "What to fix" — a severity-ranked list of the few issues worth acting on
 * right now, each row deep-linked to that issue.
 *
 * Deliberately shows a handful, not a page: the point is to answer *what do
 * I do first*, which an exhaustive table cannot. Callers pass already-sorted,
 * already-filtered issues (see `byUrgency` / `isActionable`), so this stays a
 * pure presentation component and can sit on any scope — one capture, one
 * scan, or the whole company.
 */

export interface IssueTriageListProps {
  /** Pre-sorted, pre-sliced issues to render. */
  issues: IssueInfo[];
  /** Total actionable count across the scope, for the header + footer link. */
  actionableTotal: number;
  title?: string;
  /** Where "View all N" points. Omit to hide the footer. */
  viewAllTo?: string;
  isLoading?: boolean;
  errorDetail?: string | null;
  /**
   * Suppresses the all-clear state while upstream work is still producing
   * issues, so we don't flash "No actionable issues recorded" before the worker has run.
   */
  building?: boolean;
  /** Copy for the all-clear state, tuned per scope. */
  allClearHint?: string;
}

export function IssueTriageList({
  issues,
  actionableTotal,
  title = "What to fix",
  viewAllTo,
  isLoading = false,
  errorDetail = null,
  building = false,
  allClearHint = "No actionable issues recorded here. Check scan coverage for the full picture.",
}: IssueTriageListProps): JSX.Element {
  return (
    <section
      className="flex flex-col overflow-hidden rounded-xl bg-card shadow-sm ring-1 ring-border/70"
      aria-label={title}
    >
      <header className="flex items-center justify-between border-b border-border/60 px-5 py-3">
        <div className="flex items-center gap-2">
          <AlertTriangle className="h-4 w-4 text-muted-foreground" />
          <h2 className="text-sm font-semibold text-foreground">{title}</h2>
          {issues.length > 0 ? (
            <span className="rounded-full bg-muted px-2 py-0.5 text-[10px] uppercase tracking-wide text-muted-foreground">
              most urgent {issues.length}
            </span>
          ) : null}
        </div>
        {actionableTotal > 0 ? (
          <span className="text-xs tabular-nums text-muted-foreground">
            {actionableTotal} actionable
          </span>
        ) : null}
      </header>

      {isLoading ? (
        <div className="flex-1 px-5 py-6 text-sm text-muted-foreground">Loading issues…</div>
      ) : errorDetail !== null ? (
        <div className="flex-1 px-5 py-6 text-sm text-danger">{errorDetail}</div>
      ) : issues.length === 0 ? (
        <AllClearState building={building} hint={allClearHint} />
      ) : (
        <>
          <ul className="flex-1 divide-y divide-border/60">
            {issues.map((issue) => (
              <IssueRow key={issue.id} issue={issue} />
            ))}
          </ul>
          {viewAllTo && actionableTotal > issues.length ? (
            <div className="border-t border-border/60 px-5 py-3">
              <Button asChild size="sm" variant="ghost" className="w-full justify-center">
                <Link to={viewAllTo}>
                  View all {actionableTotal} actionable issues
                  <ArrowRight className="ml-1.5 h-3.5 w-3.5" />
                </Link>
              </Button>
            </div>
          ) : null}
        </>
      )}
    </section>
  );
}

function AllClearState({ building, hint }: { building: boolean; hint: string }): JSX.Element {
  if (building) {
    return (
      <div className="flex flex-1 items-center gap-3 px-5 py-6 text-sm text-muted-foreground">
        <Sparkles className="h-4 w-4 animate-pulse text-primary" />
        <span>Analysing shelves for issues…</span>
      </div>
    );
  }
  return (
    <div className="flex flex-1 flex-col items-center justify-center gap-1 px-5 py-8 text-center">
      <Inbox className="h-8 w-8 text-muted-foreground" />
      <p className="text-sm font-semibold text-foreground">No actionable issues recorded</p>
      <p className="max-w-xs text-xs text-muted-foreground">{hint}</p>
    </div>
  );
}

function IssueRow({ issue }: { issue: IssueInfo }): JSX.Element {
  const style = SEVERITY_STYLES[issue.severity];
  const Icon = ISSUE_TYPE_ICON[issue.issueType] ?? ISSUE_TYPE_ICON.unknown;
  const typeLabel = ISSUE_TYPE_LABEL[issue.issueType] ?? ISSUE_TYPE_LABEL.unknown;
  return (
    <li className="relative">
      {/* Links straight to the issue's own page. Pointing at the filtered list
          instead means the user still has to find the row by eye. */}
      <Link
        to={`/compliance/${encodeURIComponent(issue.id)}`}
        className="group flex items-start gap-3 px-5 py-3 transition-colors hover:bg-muted/50 focus-visible:bg-muted/50 focus-visible:outline-none"
      >
        <span
          aria-hidden
          className={`absolute inset-y-2 left-0 w-1 rounded-r-full ${style.band}`}
        />
        <span
          className={`mt-0.5 inline-flex h-8 w-8 shrink-0 items-center justify-center rounded-lg ${style.iconBg} ${style.iconText}`}
        >
          <Icon className="h-4 w-4" />
        </span>
        <div className="min-w-0 flex-1">
          <div className="flex flex-wrap items-center gap-2">
            <span className="truncate text-sm font-medium text-foreground">{issue.title}</span>
            <span
              className={`inline-flex items-center rounded-full px-1.5 py-0.5 text-[10px] font-medium ${style.chipBg} ${style.chipText}`}
            >
              {style.label}
            </span>
          </div>
          <p className="mt-0.5 truncate text-[11px] text-muted-foreground">
            {typeLabel}
            {issue.storeName ? (
              <>
                <span className="mx-1.5 opacity-40">·</span>
                {issue.storeName}
              </>
            ) : null}
            {issue.description ? (
              <>
                <span className="mx-1.5 opacity-40">·</span>
                {issue.description}
              </>
            ) : null}
          </p>
        </div>
        <ArrowRight
          className="mt-1.5 h-4 w-4 shrink-0 text-muted-foreground transition-transform group-hover:translate-x-0.5 group-hover:text-foreground"
          aria-hidden
        />
      </Link>
    </li>
  );
}
